/**
 * /ndt-inspection/ai — the AI digests (batch 6, fieldiq-api#1177 / #1178).
 *
 * A full-width register of the company's stored digests (GET /ai/digests,
 * newest first, the API's latest 20), filtered by kind in the URL so a
 * record's back arrow returns to the list as it was left. A row opens the
 * digest on its own page.
 *
 * The band runs a new digest: Repair-rate and Dose review ask for an optional
 * window (blank = the API's default: 90 days / this year to date), Compliance
 * runs as of the tenant's today. A run opens on its page when it lands. Ticket
 * preflights are run from a field ticket's page and listed here too.
 *
 * Admin, office staff and supervisor only (NDT_AI_ROLES). Anyone else gets a
 * plain sentence and no request is made.
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Activity, ClipboardCheck, FileClock, Loader2, Radiation, Sparkles } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useNdtAiDigests, useRunNdtAi } from '../../hooks/useNdtAi';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { formatInstant } from '../../lib/calendar-date';
import { queryLoadFailure } from '../../lib/load-failure';
import { NDT_AI_AGENT_LABEL, canUseNdtAi, ndtAiAgentLabel, ndtAiOutcomeSkin, ndtAiScopeText } from '../../lib/ndt-ai';
import { isNdtDayInput, ndtErrorMessage } from '../../lib/ndt-inspection';
import { NDT_AI_AGENTS, type NdtAiDigest } from '../../types/ndt-ai';
import { useOpenRecord } from '../downhole/recordPage';
import { NDT_DATE_INPUT, NDT_PRIMARY, NDT_SECONDARY, NdtBadge, NdtChip, NdtChipRow, NdtDialog, NdtEmpty, NdtField, NdtFormError, NdtPanel, NdtRegister, ndtDay, type NdtColumn } from './NdtBits';

const BAND_SECONDARY =
  'inline-flex h-10 items-center gap-1.5 rounded-fi-control border border-white/25 px-4 text-sm font-bold transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60';

type WindowAgent = 'ndt_repair_rate_digest' | 'ndt_dose_review';
const WINDOW_HINT: Record<WindowAgent, string> = {
  ndt_repair_rate_digest: 'Leave blank for the last 90 days.',
  ndt_dose_review: 'Leave blank for this year to date.',
};

type Row = NdtAiDigest & { id: string };

export default function AiDigestsScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const { user } = useAuth();
  const allowed = canUseNdtAi(user?.role);
  const tz = useTenantTimeZone();
  const [params, setParams] = useSearchParams();
  const agentParam = params.get('agent') ?? '';
  const agent = (NDT_AI_AGENTS as readonly string[]).includes(agentParam) ? agentParam : '';
  const listQuery = useNdtAiDigests(agent || undefined, allowed);
  const allQuery = useNdtAiDigests(undefined, allowed);
  const run = useRunNdtAi();
  const [windowFor, setWindowFor] = useState<WindowAgent | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  const rows: Row[] = (listQuery.data ?? []).map((d) => ({ ...d, id: d.agentRunId }));
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data;
  const pendingCount = all ? all.filter((d) => d.outcome === 'pending').length : undefined;

  const setAgent = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set('agent', value);
    else next.delete('agent');
    setParams(next, { replace: true });
  };

  const opened = (id: string | undefined) => {
    if (id) openRecord(`/ndt-inspection/ai/${id}`);
  };

  const runCompliance = () => {
    setRunError(null);
    run.mutate({ agent: 'ndt_cert_expiry_digest' }, {
      onSuccess: (r) => opened(r?.agentRunId),
      onError: (err) => setRunError(ndtErrorMessage(err, 'The compliance digest did not run.')),
    });
  };

  const startWindow = (a: WindowAgent) => {
    setFrom('');
    setTo('');
    setFormError(null);
    setWindowFor(a);
  };

  const submitWindow = (e: React.FormEvent) => {
    e.preventDefault();
    if (!windowFor) return;
    setFormError(null);
    if (from && !isNdtDayInput(from)) return setFormError('From is not a date.');
    if (to && !isNdtDayInput(to)) return setFormError('To is not a date.');
    if (from && to && from > to) return setFormError('From must be on or before To.');
    const window = { ...(from ? { from } : {}), ...(to ? { to } : {}) };
    run.mutate({ agent: windowFor, window }, {
      onSuccess: (r) => {
        setWindowFor(null);
        opened(r?.agentRunId);
      },
      onError: (err) => setFormError(ndtErrorMessage(err, 'The digest did not run.')),
    });
  };

  const columns: NdtColumn<Row>[] = [
    { key: 'kind', header: 'Digest', render: (d) => <span className="font-extrabold text-white">{ndtAiAgentLabel(d.agent)}</span> },
    { key: 'covers', header: 'Covers', render: (d) => ndtAiScopeText(d.agent, d.triggerData, ndtDay) },
    { key: 'run', header: 'Run', render: (d) => formatInstant(d.createdAt, tz) ?? '—' },
    { key: 'draft', header: 'AI draft', render: (d) => (d.draft ? 'Drafted' : 'Unavailable') },
    { key: 'status', header: 'Review', render: (d) => <NdtBadge skin={ndtAiOutcomeSkin(d.outcome)} value={d.outcome} /> },
  ];

  const actions = allowed ? (
    <div className="flex flex-wrap gap-2" data-testid="ndt-ai-run-actions">
      <button type="button" className={BAND_SECONDARY} onClick={() => startWindow('ndt_repair_rate_digest')} disabled={run.isPending} data-testid="ndt-ai-run-repair">
        <Activity className="size-4" aria-hidden="true" />
        Repair-rate digest
      </button>
      <button type="button" className={BAND_SECONDARY} onClick={runCompliance} disabled={run.isPending} data-testid="ndt-ai-run-compliance">
        {run.isPending && !windowFor ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <ClipboardCheck className="size-4" aria-hidden="true" />}
        Compliance digest
      </button>
      <button type="button" className={BAND_SECONDARY} onClick={() => startWindow('ndt_dose_review')} disabled={run.isPending} data-testid="ndt-ai-run-dose">
        <Radiation className="size-4" aria-hidden="true" />
        Dose review
      </button>
    </div>
  ) : undefined;

  return (
    <div data-testid="ndt-ai-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="NDT inspection"
        title="AI Digests"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={actions}
        stats={
          allowed
            ? [
                { icon: <FileClock className="size-4" aria-hidden="true" />, label: 'To review', sub: 'Pending drafts', tone: 'accent', loading: allQuery.isLoading, value: displayValue(pendingCount) },
                { icon: <Sparkles className="size-4" aria-hidden="true" />, label: 'Digests', sub: 'Latest 20', tone: 'neutral', loading: allQuery.isLoading, value: displayValue(all ? all.length : undefined) },
              ]
            : undefined
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {!allowed ? (
            <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-6 text-sm text-fieldiq-text" data-testid="ndt-ai-not-allowed">
              AI digests are for admins, office staff and supervisors.
            </p>
          ) : (
            <>
              <p className="text-xs text-fieldiq-muted">Figures are computed exactly; the AI writes a draft summary that a person approves or dismisses.</p>
              {runError && <NdtFormError message={runError} />}
              <NdtChipRow label="Digest kind">
                <NdtChip label="All" active={!agent} onClick={() => setAgent('')} />
                {NDT_AI_AGENTS.map((a) => (
                  <NdtChip key={a} label={NDT_AI_AGENT_LABEL[a]} active={agent === a} onClick={() => setAgent(a)} />
                ))}
              </NdtChipRow>
              <NdtPanel eyebrow="AI digests" title="Digests" testId="ndt-ai-register">
                <NdtRegister
                  query={listQuery}
                  rows={rows}
                  columns={columns}
                  rail={(d) => ndtAiOutcomeSkin(d.outcome).rail}
                  onOpen={(d) => opened(d.agentRunId)}
                  subject="AI digests"
                  testId="ndt-ai-digest"
                  minWidth="min-w-[760px]"
                  empty={<NdtEmpty>{agent ? `No ${NDT_AI_AGENT_LABEL[agent as keyof typeof NDT_AI_AGENT_LABEL].toLowerCase()} has been run.` : 'No AI digest has been run yet.'}</NdtEmpty>}
                  card={(d) => (
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-extrabold text-white">{ndtAiAgentLabel(d.agent)}</p>
                        <p className="mt-0.5 text-xs text-fieldiq-muted">{ndtAiScopeText(d.agent, d.triggerData, ndtDay)}</p>
                        <p className="mt-0.5 text-xs text-fieldiq-muted">{formatInstant(d.createdAt, tz) ?? '—'}</p>
                      </div>
                      <NdtBadge skin={ndtAiOutcomeSkin(d.outcome)} value={d.outcome} />
                    </div>
                  )}
                />
              </NdtPanel>
            </>
          )}
        </div>
      </section>

      {windowFor && (
        <NdtDialog open title={`Run ${NDT_AI_AGENT_LABEL[windowFor].toLowerCase()}`} onClose={() => !run.isPending && setWindowFor(null)}>
          <form onSubmit={submitWindow} className="space-y-3" noValidate data-testid="ndt-ai-window-form">
            <NdtFormError message={formError} />
            <p className="text-xs text-fieldiq-muted">{WINDOW_HINT[windowFor]}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <NdtField label="From">
                <input type="date" className={NDT_DATE_INPUT} value={from} onChange={(e) => setFrom(e.target.value)} data-testid="ndt-ai-from" />
              </NdtField>
              <NdtField label="To">
                <input type="date" className={NDT_DATE_INPUT} value={to} onChange={(e) => setTo(e.target.value)} data-testid="ndt-ai-to" />
              </NdtField>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className={NDT_SECONDARY} onClick={() => setWindowFor(null)} disabled={run.isPending}>
                Cancel
              </button>
              <button type="submit" className={NDT_PRIMARY} disabled={run.isPending} data-testid="ndt-ai-window-run">
                {run.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
                {run.isPending ? 'Running…' : 'Run'}
              </button>
            </div>
          </form>
        </NdtDialog>
      )}
    </div>
  );
}
